import { CheckoutRequestSchema, errorMessageVi, type CheckoutRequest } from '@irace/shared';
import type { SubmitOutcome } from '../api/client.ts';
import { CHECKOUT_SCHEMA_VERSION, type CheckoutRecord } from '../db/checkout-record.ts';
import type { CheckoutStore } from '../db/indexeddb.ts';
import { isRetryableErrorCode } from '../db/outbox-record.ts';
import type { TurnstileExecutor } from '../turnstile/client.ts';
import { LocalSaveError } from './outbox.ts';

/**
 * G-012 — save-before-network check-out service.
 *
 * `SubmissionService` step for step, and deliberately so: a departure is saved
 * to IndexedDB before any request is made (AGENTS.md invariant 2), the record
 * never holds a Turnstile token (invariant 3), and every attempt reuses the
 * check-in's submission_id with a freshly minted token (invariant 4).
 *
 * Turnstile constraint for whoever wires the UI: the check-out widget must be
 * rendered with the *same* action as check-in (`event_checkin`, the default in
 * `TurnstileWidget`). `POST /api/v1/checkouts` verifies against one configured
 * `TURNSTILE_ACTION` shared with check-in, so a widget rendered with any other
 * action mints tokens Siteverify reports as `action-mismatch` — a 403 on every
 * single check-out that no one at the venue can diagnose or fix. This service
 * never chooses an action; it only consumes a `TurnstileExecutor`.
 */

export interface CheckoutInput {
  /** The check-in's submission_id. A check-out never mints its own. */
  submissionId: string;
  eventId: string;
}

/**
 * What a send attempt did to the check-out record.
 *
 * Its own type rather than a widened `SendResult`: that one carries an
 * `OutboxRecord` and is consumed by the retry engine that protects pending
 * check-ins, which must not be made to reason about a second record shape.
 */
export type CheckoutSendResult =
  | { kind: 'accepted'; record: CheckoutRecord }
  | { kind: 'pending'; record: CheckoutRecord; retryAfterSeconds?: number }
  | { kind: 'verify_retry'; record: CheckoutRecord }
  | { kind: 'rejected'; record: CheckoutRecord; code: string; message: string };

export interface CheckoutServiceDeps {
  store: CheckoutStore;
  turnstile: TurnstileExecutor;
  submit: (request: CheckoutRequest) => Promise<SubmitOutcome>;
  now?: () => Date;
  isOnline?: () => boolean;
}

/** Build the wire request. The token lives only in this object, in memory. */
export function toCheckoutRequest(record: CheckoutRecord, turnstileToken: string): CheckoutRequest {
  return {
    submission_id: record.submissionId,
    event_id: record.eventId,
    client_checked_out_at: record.clientCheckedOutAt,
    turnstile_token: turnstileToken,
  };
}

export { LocalSaveError };

export class CheckoutService {
  readonly #deps: Required<Omit<CheckoutServiceDeps, 'store' | 'turnstile' | 'submit'>> &
    CheckoutServiceDeps;

  constructor(deps: CheckoutServiceDeps) {
    this.#deps = {
      ...deps,
      now: deps.now ?? (() => new Date()),
      isOnline: deps.isOnline ?? (() => navigator.onLine),
    };
  }

  /**
   * Record the departure locally. Nothing here touches the network, so a
   * failure to persist stops the check-out rather than producing a send whose
   * record would be invisible after a reload.
   *
   * The timestamp is the device's, not the server's: the participant tapped
   * the button at this moment, and a device with no signal must still be able
   * to say when.
   */
  async save(input: CheckoutInput): Promise<CheckoutRecord> {
    const record: CheckoutRecord = {
      schemaVersion: CHECKOUT_SCHEMA_VERSION,
      submissionId: input.submissionId,
      eventId: input.eventId,
      clientCheckedOutAt: this.#deps.now().toISOString(),
      status: 'pending',
      attemptCount: 0,
    };

    try {
      await this.#deps.store.put(record);
    } catch (error) {
      throw new LocalSaveError(error);
    }

    return record;
  }

  /**
   * Turnstile, then POST. Always called with a record that is already on disk,
   * and always with that record's submission_id — a retry never mints a new
   * one (AGENTS.md invariant 4).
   */
  async send(record: CheckoutRecord): Promise<CheckoutSendResult> {
    if (!this.#deps.isOnline()) {
      // Nothing to mark: the record is already pending, and burning an attempt
      // on a device with no radio would only inflate the backoff.
      return { kind: 'pending', record };
    }

    const sending = (await this.#deps.store.markSending(record.submissionId)) ?? record;

    let token: string;
    try {
      token = await this.#deps.turnstile.execute();
    } catch {
      // The challenge itself did not complete. The record stays retryable and
      // the participant gets the "verify and resend" affordance.
      await this.#deps.store.markFailed(sending.submissionId, 'TURNSTILE_CLIENT');
      return { kind: 'verify_retry', record: { ...sending, status: 'pending' } };
    }

    const request = CheckoutRequestSchema.safeParse(toCheckoutRequest(sending, token));
    if (!request.success) {
      // The local record cannot produce a valid request, so retrying is
      // pointless — fail it here rather than making the API say so.
      await this.#deps.store.markFailed(sending.submissionId, 'INVALID_REQUEST');
      return {
        kind: 'rejected',
        record: { ...sending, status: 'failed' },
        code: 'INVALID_REQUEST',
        message: errorMessageVi('INVALID_REQUEST'),
      };
    }

    const outcome = await this.#deps.submit(request.data);

    if (outcome.kind === 'accepted') {
      await this.#deps.store.markAccepted(sending.submissionId);
      return { kind: 'accepted', record: { ...sending, status: 'accepted' } };
    }

    await this.#deps.store.markFailed(sending.submissionId, outcome.code);

    if (outcome.code === 'TURNSTILE_FAILED') {
      // The token failed, the check-out did not. Reset so the next attempt
      // carries a token Siteverify has never seen.
      this.#deps.turnstile.reset();
      return { kind: 'verify_retry', record: { ...sending, status: 'pending' } };
    }

    if (!isRetryableErrorCode(outcome.code)) {
      // Includes CHECKOUT_NOT_ENABLED: an event that does not accept
      // departures will not start accepting them because a device asked again.
      return {
        kind: 'rejected',
        record: { ...sending, status: 'failed' },
        code: outcome.code,
        message: outcome.message,
      };
    }

    return {
      kind: 'pending',
      record: { ...sending, status: 'pending' },
      ...(outcome.retryAfterSeconds !== undefined
        ? { retryAfterSeconds: outcome.retryAfterSeconds }
        : {}),
    };
  }
}
